import {
  Alert,
  AlertDescription,
  AlertTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from 'design-system';

import { useUserDetails } from '@/features/_shared/user';

import type { FeedFilters } from './api/fetchFeedData';
import { FeedArticleDialog } from './components/FeedArticleDialog';
import { FeedRow } from './components/FeedRow';
import { FeedSkeleton } from './components/FeedSkeleton';
import { useMonthlyRecapsPromotion } from './hooks/useMonthlyRecapsPromotion';
import { useFeedData } from './queries/useFeedData';

const FeedEmptyState = () => (
  <Empty className='min-h-40 border-0'>
    <EmptyHeader>
      <EmptyTitle>No feed updates available.</EmptyTitle>
    </EmptyHeader>
  </Empty>
);

const FeedErrorState = () => (
  <Alert>
    <AlertTitle>Feed updates are unavailable.</AlertTitle>
    <AlertDescription>Try again shortly.</AlertDescription>
  </Alert>
);

const FeedRestrictedState = () => (
  <Empty className='min-h-40 border-0'>
    <EmptyHeader>
      <EmptyTitle>Feed access expired</EmptyTitle>
      <EmptyDescription>Upgrade your plan to access Feed updates.</EmptyDescription>
    </EmptyHeader>
  </Empty>
);

export const Feed = ({ metadata }: { metadata: FeedFilters }) => {
  const { isUserExpired } = useUserDetails();
  const { data, isError, isLoading, isSuccess } = useFeedData(metadata);
  const hasNoData = isSuccess && data?.length === 0;
  const { articleDialogCTAValue, setArticleDialogCTAValue } = useMonthlyRecapsPromotion(data);

  return (
    <section
      aria-label='Market feed'
      className='flex max-h-[650px] min-h-[200px] flex-col gap-5 overflow-y-auto px-2 py-5'
    >
      {isUserExpired ? (
        <FeedRestrictedState />
      ) : (
        <>
          {isLoading ? <FeedSkeleton /> : null}
          {isError ? <FeedErrorState /> : null}
          {hasNoData ? <FeedEmptyState /> : null}

          {data?.map(row => (
            <FeedRow
              key={row.transactionTime + row.values.Title}
              {...row}
            />
          ))}

          {articleDialogCTAValue ? (
            <FeedArticleDialog
              CTAValue={articleDialogCTAValue}
              open
              onClose={() => {
                setArticleDialogCTAValue(null);
              }}
            />
          ) : null}
        </>
      )}
    </section>
  );
};
